/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import React, { useContext, useEffect, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { ARCHIVE_SUCCESS_REDIRECT_ROUTES } from '@confluence/content-types-utils/entry-points/contentTypeConstants';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { SPACE_ARCHIVED_PAGES, SPACE_OVERVIEW } from '@confluence/named-routes';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy';

import type {
	ArchivePagesLoaderSuccessQuery as ArchivePagesLoaderSuccessQuery$data,
	ArchivePagesLoaderSuccessQueryVariables as ArchivePagesLoaderSuccessQuery$variables,
} from './__types__/ArchivePagesLoaderSuccessQuery';
import { CONTENT_STATUS_ARCHIVED } from './archivingConstants';

type ArchivePagesLoaderSuccessProps = {
	contentId: string;
	pageBeingViewedId: string;
	pageBeingViewedLastStatus: string;
};

export function ArchivePagesLoaderSuccess({
	contentId,
	pageBeingViewedId,
	pageBeingViewedLastStatus,
}: ArchivePagesLoaderSuccessProps) {
	const { getHref, matchRoute, push, setQueryParams } = useContext(RoutesContext);
	const route = matchRoute(getHref());
	const { routePolicy } = useNavigationPolicy();
	const { data, error, loading } = useQuery<
		ArchivePagesLoaderSuccessQuery$data,
		ArchivePagesLoaderSuccessQuery$variables
	>(
		gql`
			query ArchivePagesLoaderSuccessQuery(
				$archivedPageId: ID!
				$pageBeingViewedId: ID!
				$hasPageBeingViewedId: Boolean!
			) {
				singleContent(id: $archivedPageId) {
					ancestors {
						id
						status
					}
					archivedContentMetadata {
						restoreParent {
							id
							status
							links {
								context
								webui
							}
						}
					}
					id
					space {
						homepage {
							id
						}
						alias
						id
						key
					}
					status
				}
				pageBeingViewed: singleContent(id: $pageBeingViewedId) @include(if: $hasPageBeingViewedId) {
					id
					status
				}
				currentPage: ptpage(
					id: $archivedPageId
					pageTree: 500
					status: [CURRENT]
					enablePaging: true
				) {
					id
					status
				}
			}
		`,
		{
			errorPolicy: 'all',
			fetchPolicy: 'network-only',
			variables: {
				archivedPageId: contentId,
				pageBeingViewedId: pageBeingViewedId || '',
				hasPageBeingViewedId: Boolean(pageBeingViewedId),
			},
		},
	);
	const [pageTreeContentIdToLoad, setPageTreeContentIdToLoad] = useState<string | null>(null);
	const [pageTreeUpdateFired, setPageTreeUpdateFired] = useState<boolean>(false);

	const [isArchiveSuccessProcessComplete, setIsArchiveSuccessProcessComplete] =
		useState<boolean>(false);
	useEffect(() => {
		if (!loading && !isArchiveSuccessProcessComplete) {
			const archivedPageStatus = data?.singleContent?.status;
			if (archivedPageStatus === CONTENT_STATUS_ARCHIVED) {
				const spaceKey = data?.singleContent?.space?.key;
				const spaceAlias = data?.singleContent?.space?.alias || spaceKey;
				const spaceHomepageId = data?.singleContent?.space?.homepage?.id;
				const restoreParent = data?.singleContent?.archivedContentMetadata?.restoreParent;
				let archivedPageAncestorContentId: string | undefined;
				if (restoreParent && restoreParent.id && restoreParent.status !== CONTENT_STATUS_ARCHIVED) {
					archivedPageAncestorContentId = restoreParent.id;
				}
				/**
				 * Always update the page tree state
				 *  - If possible, use the closest surviving ancestor
				 *  - Next, try the space overview
				 *  - If there's no space overview, we use the contentId in order to force the page tree to refresh.
				 *    cc-page-tree will return null for this value, not an error.  The result should be an updated
				 *    page tree with no page highlighted.
				 */
				setPageTreeContentIdToLoad(archivedPageAncestorContentId || spaceHomepageId || contentId);

				/**
				 * Check if the page being viewed was archived.
				 * If it's still current or it was already archived, there's no need to redirect.
				 * Examples:
				 *  - Archive the page you are viewing
				 *     + spage you are viewing status flips from current -> archived
				 *     + redirect to parent
				 *  - Archive an ancestor of the page you are viewing, and the archive INCLUDES nested pages
				 *     + page you are viewing status flips from current -> archived
				 *     + redirect to the highest non-archived parent (restoreParent of the page you archived)
				 *  - Archive an ancestor of the current page you are viewing, and the archive does NOT include children
				 *     + page you are viewing status remains current
				 *     + page you are viewing will be re-located in the page tree and you will not be redirected
				 *  - Archive a cousin page in the page tree to the page you are viewing
				 *     + page you are viewing status remains current and you will not be redirected
				 */
				if (route?.name && ARCHIVE_SUCCESS_REDIRECT_ROUTES.includes(route.name)) {
					const pageBeingViewedNewStatus = data?.pageBeingViewed?.status;
					if (
						pageBeingViewedNewStatus === CONTENT_STATUS_ARCHIVED &&
						pageBeingViewedLastStatus !== CONTENT_STATUS_ARCHIVED
					) {
						let nextHref: string | undefined;
						/**
						 * If the last surviving ancestor exists and isn't the space overview, redirect to it.
						 * Otherwise, always try to redirect to the space index. Either
						 *  - The space overview is actually the last surviving ancestor.
						 *  - The page being archived is an orphan. If there's no overview then users will see the space
						 *    overview's empty state.
						 */
						if (
							archivedPageAncestorContentId &&
							archivedPageAncestorContentId !== spaceHomepageId &&
							restoreParent?.links?.context &&
							restoreParent?.links?.webui
						) {
							nextHref = restoreParent.links.context + restoreParent.links.webui;
						} else {
							nextHref = SPACE_OVERVIEW.toUrl(
								{ spaceKey: spaceAlias },
								{
									query: spaceHomepageId
										? {
												[SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY]: spaceHomepageId,
											}
										: undefined,
								},
							);
						}
						if (nextHref && nextHref !== getHref()) {
							// For Confluence routePolicy will be undefined, hence we can proceed with re-direction==push.
							// For Embedded Pages, since routePolicy will be defined, we will not push as Parent Product
							// will handle the navigation.
							if (!routePolicy) {
								push(nextHref);
							}
						}
					}
				}
				if (route?.name === SPACE_ARCHIVED_PAGES.name) {
					// TODO: CCCAXP-595 - This may not do anything; investigate and remove!
					setQueryParams({ reload: true });
				}
				setIsArchiveSuccessProcessComplete(true);
			}
		}
	}, [
		data,
		loading,
		route,
		getHref,
		push,
		contentId,
		routePolicy,
		setPageTreeContentIdToLoad,
		pageBeingViewedLastStatus,
		isArchiveSuccessProcessComplete,
		setIsArchiveSuccessProcessComplete,
		setQueryParams,
	]);

	return error ? (
		<ErrorDisplay error={error} />
	) : (
		<Subscribe to={[PageTreeStateUpdater]}>
			{(pageStateUpdater: PageTreeStateUpdater) => {
				if (!pageTreeUpdateFired && pageTreeContentIdToLoad) {
					// only request the page tree update once
					pageStateUpdater.setForceFetchPageId(pageTreeContentIdToLoad);
					setPageTreeUpdateFired(true);
				}
				return null;
			}}
		</Subscribe>
	);
}
